import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Categories.css';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCategories } from '../../hooks/useCategories';
import { EmptyState, SectionHeader } from '../UI';
import { getCategoryImageUrl, getProductId } from '../../utils/helpers';

// Fallback images for categories without images
import bonkid from '../Assets/bonkid.jpg';
import cool from '../Assets/cool.jpg';
import deoimage from '../Assets/deoimage.jpg';
import heropic from '../Assets/heropic.jpg';
import metisse from '../Assets/metisse.jpeg';
import classe from '../Assets/classe.jpg';
import beau from '../Assets/beau.jpg';

const fallbackImages = [cool, bonkid, deoimage, metisse, heropic, classe, beau];

const Categories = () => {
  const { t } = useTranslation('common');
  const scrollRef = useRef(null);
  const { data: categories, isLoading, error } = useCategories();

  const scroll = (direction) => {
    const amount = 250;
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ 
        left: direction === 'left' ? -amount : amount, 
        behavior: 'smooth' 
      });
    }
  };

  if (isLoading) {
    return (
      <section className="categories-section">
        <div className="categories-container">
          <SectionHeader title={t('explorePopularCategories', { defaultValue: 'Explore popular categories' })} />
          <div className="categories-slider">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="category-card category-skeleton">
                <div className="category-image-wrapper skeleton-circle"></div>
                <span className="skeleton-text"></span>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="categories-section">
        <div className="categories-container">
          <SectionHeader title={t('explorePopularCategories', { defaultValue: 'Explore popular categories' })} />
          <p className="error-message">{t('failedToLoadCategories', { defaultValue: 'Failed to load categories.' })}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="categories-section">
      <div className="categories-container">
        <SectionHeader title={t('explorePopularCategories', { defaultValue: 'Explore popular categories' })}>
          <div className="nav-buttons">
            <button type="button" className="nav-btn" onClick={() => scroll('left')} aria-label={t('previous', { defaultValue: 'Previous' })}>
              <ChevronLeft size={20} />
            </button>
            <button type="button" className="nav-btn" onClick={() => scroll('right')} aria-label={t('next', { defaultValue: 'Next' })}>
              <ChevronRight size={20} />
            </button>
          </div>
        </SectionHeader>

        <div className="categories-slider" ref={scrollRef}>
          {categories?.length > 0 ? (
            categories.map((category, index) => (
              <Link 
                to={`/category/${getProductId(category)}`} 
                key={getProductId(category) || index} 
                className="category-card"
              >
                <div className="category-image-wrapper">
                  <img 
                    src={getCategoryImageUrl(category, fallbackImages[index % fallbackImages.length])} 
                    alt={category.name} 
                    className="category-image" 
                  />
                </div>
                <span className="category-name">{category.name}</span>
              </Link>
            ))
          ) : (
            <EmptyState
              icon="search"
              title={t('empty.noCategoriesTitle')}
              description={t('empty.noCategoriesDescription')}
              actionText={t('empty.exploreShop')}
              actionLink="/search"
              variant="inline"
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default Categories;
